import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  evaluateTemplateValueCollection,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

/**
 * Reactive-json primitive — renders an <a> tag whose href is built by
 * interpolating `:placeholder` tokens in a `template` string with values
 * resolved from the reactive store.
 *
 * Reactive-json's attribute interpolation can't concat segments inside an
 * HTML attribute (e.g. `href="/test/{slug}"` from a `~.slug` ref), so any
 * "build URL per row with a fixed prefix" use-case lands here. The
 * URL prefix and the placeholder names are entirely block-defined — no
 * hardcoded `/test/` or `/article/` in the front bundle.
 *
 * YAML usage :
 *   - type: PathLink
 *     template: "/test/:slug"             # block decides its own pattern
 *     values:
 *       slug: ~.slug                       # placeholder → ref
 *     attributes:                          # any HTML attrs (class, target, …)
 *       class: "btn-primary"
 *       target: _blank
 *     content: "Voir"
 *
 * Multi-placeholder example :
 *   - type: PathLink
 *     template: "/article/:year/:slug"
 *     values:
 *       year: ~.objectData.data.year
 *       slug: ~.slug
 *
 * Query string (paramètres composés, liens partageables) :
 *   - type: PathLink
 *     template: "/catalogue/:id/fiche"
 *     values: { id: ~~.id }
 *     query:
 *       tab: ~.key                         # scalaire → ?tab=prix
 *       tags: ~~.tags                      # tableau  → &tags=a&tags=b
 *   Une valeur vide (null, '', []) n'émet aucun paramètre : un filtre non
 *   renseigné ne doit pas apparaître dans l'adresse. C'est le pendant, côté
 *   lien, de ce que `overlayUrlParams` relit à l'arrivée (clé répétée = liste).
 *
 * Lien courant (onglets de navigation, fil d'Ariane) :
 *     current: { value: ~.key, match: ~~.tab }
 *   pose `aria-current="page"` quand les deux valeurs sont égales (comparées en
 *   chaînes). Un attribut conditionnel ne s'écrit pas en YAML sur ce composant
 *   (`attributeTransforms` n'existe que sur les éléments HTML du cœur).
 *
 * Notes :
 *   • Placeholder values are URL-encoded.
 *   • An empty / undefined value for a placeholder leaves the token in the
 *     output (`/test/:slug`) — easier to spot the bug than silently
 *     producing `/test/`.
 *   • If `template` is missing, falls back to `href="#"` so the link is
 *     a noop instead of crashing.
 */
export const PathLink = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const template = evaluateTemplateValue({
    valueToEvaluate: props.template,
    globalDataContext,
    templateContext,
  })

  const values = evaluateTemplateValueCollection({
    valueToEvaluate: props.values ?? {},
    globalDataContext,
    templateContext,
  })

  let href = typeof template === 'string' && template.length > 0 ? template : '#'
  if (values && typeof values === 'object') {
    for (const [key, val] of Object.entries(values)) {
      if (val == null || val === '') continue
      href = href.replace(
        new RegExp(`:${key}(?![A-Za-z0-9_])`, 'g'),
        encodeURIComponent(String(val)),
      )
    }
  }

  const query = evaluateTemplateValueCollection({
    valueToEvaluate: props.query ?? {},
    globalDataContext,
    templateContext,
  })
  if (query && typeof query === 'object' && href !== '#') {
    const params = new URLSearchParams()
    for (const [key, val] of Object.entries(query)) {
      const list = Array.isArray(val) ? val : [val]
      for (const item of list) {
        if (item == null || item === '' || typeof item === 'object') continue
        params.append(key, String(item))
      }
    }
    const qs = params.toString()
    if (qs) href += (href.includes('?') ? '&' : '?') + qs
  }

  const evaluatedAttrs = useEvaluatedAttributes(props.attributes)
  const current = props.current && typeof props.current === 'object'
    ? evaluateTemplateValueCollection({ valueToEvaluate: props.current, globalDataContext, templateContext })
    : null
  // Une valeur VIDE est une valeur : l'onglet « Tous » (`value: ""`) est courant
  // quand le filtre est vide. Seule une valeur non déclarée ne concorde jamais.
  const isCurrent = current != null && current.value != null
    && String(current.value) === String(current.match ?? '')
  const { target } = evaluatedAttrs
  const rel = target === '_blank' ? (evaluatedAttrs.rel ?? 'noopener noreferrer') : evaluatedAttrs.rel

  return (
    <ActionDependant {...props}>
      <a {...evaluatedAttrs} href={href} rel={rel} aria-current={isCurrent ? 'page' : undefined}>
        {props.content && (
          <View
            currentData={currentData?.content}
            datafield="content"
            path={path + '.content'}
            props={props.content}
          />
        )}
      </a>
    </ActionDependant>
  )
}
